import React from "react";

import { dropdownLabel } from "../../../../shared/locationDropdowns";
import {
  formatDate,
  formatDateWithoutParsing,
} from "../../../../shared/utility";

function formatReviewValue(value: any) {
  if (value == null || value === "") return "";
  if (value instanceof Date) {
    return formatDateWithoutParsing(value);
  }
  return value;
}

function ReviewField({ label, value }: { label: string; value: any }) {
  const displayValue = formatReviewValue(value);

  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{displayValue || "—"}</dd>
    </div>
  );
}

function ReviewSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="p-4 card-custom">
      <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</dl>
    </div>
  );
}

function getOptionLabel(option: any) {
  if (option == null) return "";
  if (typeof option === "string" || typeof option === "number")
    return String(option);
  return option.label ?? option.name ?? "";
}

function getMultiOptionLabels(options: any) {
  if (!Array.isArray(options) || options.length === 0) return "";
  return options
    .map((item) => getOptionLabel(item))
    .filter(Boolean)
    .join(", ");
}

interface ManufactureCreateReviewStepProps {
  values: any;
  recipes: any[];
  certifiedByOptions: any[];
  claimsOptions: any[];
}

export default function ManufactureCreateReviewStep(
  props: ManufactureCreateReviewStepProps,
) {
  const { values, recipes, certifiedByOptions, claimsOptions } = props;

  const recipeId = values.recipe_id ?? values.selected_recipe;
  const chosenRecipe = recipeId
    ? recipes.find((el) => el.value === recipeId)
    : null;

  const certificationLabel =
    certifiedByOptions?.find(
      (opt) => opt.value === values.certification_type?.value,
    )?.label ?? getOptionLabel(values.certification_type);

  const claimsLabels =
    values.claims?.length > 0
      ? values.claims
          .map(
            (claim: any) =>
              claimsOptions?.find((opt) => opt.value === claim.value)?.label ??
              getOptionLabel(claim),
          )
          .filter(Boolean)
          .join(", ")
      : "";

  return (
    <div className="grid grid-cols-1 gap-4">
      <ReviewSection title="Setup">
        <ReviewField
          label="Production date"
          value={
            values.production_date instanceof Date
              ? formatDateWithoutParsing(values.production_date)
              : formatDate(values.production_date)
          }
        />
        <ReviewField
          label="Supervisor"
          value={getOptionLabel(values.supervisor)}
        />
        <ReviewField
          label="Employees"
          value={getMultiOptionLabels(values.employee_1)}
        />
        <ReviewField label="Location" value={dropdownLabel(values.location)} />
        <ReviewField
          label="Production room"
          value={dropdownLabel(values.production_room)}
        />
        <ReviewField label="Claims" value={claimsLabels} />
        <ReviewField label="Certified by" value={certificationLabel} />
        <ReviewField
          label="Production room temp (°C)"
          value={values.production_room_temperature}
        />
        <ReviewField
          label="Raw product temp (°C)"
          value={values.raw_product_temperature}
        />
      </ReviewSection>

      <ReviewSection title="Product">
        <ReviewField label="PLU" value={values.plu} />
        <ReviewField label="Product name" value={values.product_name} />
        <ReviewField label="Description" value={values.description_en} />
        <ReviewField label="Lot number" value={values.lot_number} />
        <ReviewField
          label="Export eligible"
          value={getOptionLabel(values.export_eligible)}
        />
        <ReviewField label="Allergens (EN)" value={values.allergens_en} />
      </ReviewSection>

      <ReviewSection title="Recipe and weights">
        <ReviewField label="Recipe" value={chosenRecipe?.label ?? ""} />
        <ReviewField label="Total target weight" value={values.new_weight} />
        <ReviewField
          label="Total raw material"
          value={values.required_total_raw_material}
        />
        <ReviewField
          label="Product temp (°C)"
          value={values.new_product_temperature}
        />
        <ReviewField label="Status" value={getOptionLabel(values.status)} />
      </ReviewSection>
    </div>
  );
}
